/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Soonki Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 25 2023
 **/

import { useCallback } from "react";
import { useAtomCallback } from "jotai/utils";
import { useAtomValue } from "jotai";
import { has } from "lodash-es";

import { JotaiStore } from "@/frameworks/libraries/commons";
import { popoverListAtom } from "@/frameworks/core/components";

import { displayedPopoversAtom } from "../../components/Popover/private/atom";
import { GROUP_POPOVER_ID, POPOVER_ID } from "../../commons";

type CommonPopoverType = {
	content: React.ReactNode;
	position?: { x: number; y: number };
	anchorEl?: Element | null;
	direction?: { x: "left" | "right" | "center"; y: "top" | "bottom" | "center" };
	overflow?: boolean;
	shouldHideOnClickOutside?: boolean;
	hasMouseClickAction?: boolean; // TODO 추가로 후에 마우스 클릭시 위치 처리가 생길 가능성도 있을 수 있음.
	zIndex?: number;
};

export type SinglePopoverType = CommonPopoverType & {
	popoverID: POPOVER_ID;
};

export type GroupPopoverType = CommonPopoverType & {
	popoverID: GROUP_POPOVER_ID;
	uniqueID: string;
};

export type popoverType<ID extends POPOVER_ID | GROUP_POPOVER_ID> = ID extends POPOVER_ID
	? SinglePopoverType
	: GroupPopoverType;

interface usePopover {
	/**
	 * 인자로 넘겨받은 popover 컨텐츠 표시 (표시는 id 기준으로 합니다.)
	 * @param popover
	 */
	show<ID extends POPOVER_ID | GROUP_POPOVER_ID>(popover: popoverType<ID>): void;
	showOnlyOnce<ID extends POPOVER_ID | GROUP_POPOVER_ID>(popover: popoverType<ID>): void;
	/**
	 * 인자로 넘겨받은 id를 지닌 popover를 hide 처리
	 * @param id
	 */
	hide(popoverID: POPOVER_ID): void;
	hide(popoverID: GROUP_POPOVER_ID, uniqueID: string): void;
	hide<ID extends POPOVER_ID | GROUP_POPOVER_ID>(id: ID): void;
	// hide(id: string, uniqueID: string): void;
	/**
	 * popover의 열림 상태에 따라 show hide 처리
	 * @param popover
	 */
	toggle<ID extends POPOVER_ID | GROUP_POPOVER_ID>(popover: popoverType<ID>): void;
	/**
	 * 모든 popover hide 처리
	 */
	hideAll(): void;
	/**
	 * 인자로 넘겨받은 id를 지닌 popover 상태를 반환하는 action 함수 (렌더링 영향 X)
	 * @param id
	 */
	getPopoverState<ID extends POPOVER_ID | GROUP_POPOVER_ID>(
		popoverID: ID,
		uniqueID?: string | undefined
	):
		| {
				isOpen: boolean;
				popover: GroupPopoverType;
		  }
		| {
				isOpen: boolean;
				popover: SinglePopoverType;
		  };

	/**
	 *  현재 열려있는 모든 팝오버 배열을 반환하는 함수
	 */
	getAll: () => (SinglePopoverType | GroupPopoverType)[];

	/**
	 * 인자를 넘겨받은 id를 지닌 popover 상태를 반환하는 훅 (렌더링 영향 O)
	 */
	usePopoverValue(
		popoverID: POPOVER_ID | GROUP_POPOVER_ID,
		uniqueID?: string
	):
		| {
				isOpen: boolean;
				popover: GroupPopoverType;
		  }
		| {
				isOpen: boolean;
				popover: SinglePopoverType;
		  };
}

function checkIsGroupPopover(popoverID: POPOVER_ID | GROUP_POPOVER_ID): popoverID is GROUP_POPOVER_ID {
	return has(GROUP_POPOVER_ID, popoverID);
}

export function usePopover(): usePopover {
	const show = useAtomCallback(
		useCallback((_, set, popover: popoverType<POPOVER_ID | GROUP_POPOVER_ID>) => {
			if (checkIsGroupPopover(popover.popoverID)) {
				const popoverWithUniqueID = popover as GroupPopoverType;
				if (getPopoverState(popoverWithUniqueID.popoverID, popoverWithUniqueID.uniqueID).isOpen) return;
				set(popoverListAtom, (popoverList) => {
					const result = popoverList.length === 0 ? [popover] : [...popoverList, popover];
					return result;
				});

				return;
			}

			if (getPopoverState(popover.popoverID).isOpen) return;
			set(popoverListAtom, (popoverList) => {
				const result = popoverList.length === 0 ? [popover] : [...popoverList, popover];
				return result;
			});
		}, []),
		{ store: JotaiStore }
	);

	const showOnlyOnce = useAtomCallback(
		useCallback((get, set, popover: popoverType<POPOVER_ID | GROUP_POPOVER_ID>) => {
			const displayedPopovers = get(displayedPopoversAtom);
			if (displayedPopovers.includes(popover.popoverID)) {
				return;
			}
			if (checkIsGroupPopover(popover.popoverID)) {
				const popoverWithUniqueID = popover as GroupPopoverType;
				if (getPopoverState(popoverWithUniqueID.popoverID, popoverWithUniqueID.uniqueID).isOpen) return;

				set(popoverListAtom, (popoverList) => {
					const result = popoverList.length === 0 ? [popover] : [...popoverList, popover];
					return result;
				});
				set(displayedPopoversAtom, (prev) => [...prev, popover.popoverID]);
				return;
			}
			if (getPopoverState(popover.popoverID).isOpen) return;
			set(popoverListAtom, (popoverList) => {
				const result = popoverList.length === 0 ? [popover] : [...popoverList, popover];
				return result;
			});
			set(displayedPopoversAtom, (prev) => [...prev, popover.popoverID]);
		}, []),
		{ store: JotaiStore }
	);

	const hide = useAtomCallback(
		useCallback((get, set, popoverID: POPOVER_ID | GROUP_POPOVER_ID, uniqueID?: string) => {
			const popoverList = get(popoverListAtom);
			if (checkIsGroupPopover(popoverID)) {
				const filteredList = popoverList.filter((popover) => {
					if (popoverID !== popover.popoverID) return true;
					if (checkIsGroupPopover(popover.popoverID)) {
						const popoverWithUniqueID = popover as GroupPopoverType;
						if (uniqueID) {
							return popoverWithUniqueID.uniqueID !== uniqueID;
						}
						return false;
					}
					return true;
				});
				set(popoverListAtom, filteredList);
			} else {
				const filteredList = popoverList.filter((el) => el.popoverID !== popoverID);
				set(popoverListAtom, filteredList);
			}
		}, []),
		{ store: JotaiStore }
	);

	const toggle = useCallback(
		(popover: popoverType<POPOVER_ID | GROUP_POPOVER_ID>) => {
			if (checkIsGroupPopover(popover.popoverID)) {
				const popoverWithUniqueID = popover as GroupPopoverType;
				if (getPopoverState(popoverWithUniqueID.popoverID, popoverWithUniqueID.uniqueID).isOpen) {
					hide(popoverWithUniqueID.popoverID, popoverWithUniqueID.uniqueID);
				} else {
					show(popoverWithUniqueID);
				}
				return;
			}
			if (getPopoverState(popover.popoverID).isOpen) {
				hide(popover.popoverID);
			} else {
				show(popover);
			}
		},
		[hide, show]
	);

	const hideAll = useAtomCallback(
		useCallback((_, set) => {
			set(popoverListAtom, []);
		}, []),
		{ store: JotaiStore }
	);

	const getPopoverState = useAtomCallback(
		(get, _, popoverID: POPOVER_ID | GROUP_POPOVER_ID, uniqueID?: string) => {
			const popoverList = get(popoverListAtom);
			if (checkIsGroupPopover(popoverID)) {
				const popover = popoverList.find(
					(popover) => popover.popoverID === popoverID && popover.uniqueID === uniqueID
				) as popoverType<GROUP_POPOVER_ID>;
				return { isOpen: popover !== undefined, popover };
			}

			const popover = popoverList.find((popover) => popover.popoverID === popoverID) as popoverType<POPOVER_ID>;
			return { isOpen: popover !== undefined, popover };
		},
		{ store: JotaiStore }
	);

	const getAll = useAtomCallback(
		(get) => {
			const popoverList = get(popoverListAtom);
			return popoverList;
		},
		{ store: JotaiStore }
	);

	const usePopoverValue = (popoverID: POPOVER_ID | GROUP_POPOVER_ID, uniqueID?: string) => {
		const popoverList = useAtomValue(popoverListAtom);
		if (checkIsGroupPopover(popoverID)) {
			const popover = popoverList.find(
				(popover) => popover.popoverID === popoverID && popover.uniqueID === uniqueID
			) as popoverType<GROUP_POPOVER_ID>;
			return { isOpen: popover !== undefined, popover };
		}

		const popover = popoverList.find((popover) => popover.popoverID === popoverID) as popoverType<POPOVER_ID>;
		return { isOpen: popover !== undefined, popover };
	};

	return { show, showOnlyOnce, getPopoverState, getAll, hide, hideAll, toggle, usePopoverValue };
}
